import React, {useState} from "react";
import {Row, Text, Button, TextArea, CheckBox} from "react-vcomponents";
import {GetViewportWorldRect} from "../../OutlinerUI/ObjectEntryUI.js";

/** Calculates the JSON for the current camera view-rect (minX/minY/maxX/maxY in world coords).
 *  Returns null if `includeCamView` is false (or the viewport-rect is unavailable).
 *
 *  **2026-08-02 — include the current camera view-rect in the export** so the test can use
 *  the exact same view dimensions as the user's live map. Without this, the test must guess
 *  the view dimensions from the zoom level — which can differ from the live app's actual
 *  viewport (the bug is zoom-sensitive: it only surfaces at specific view sizes). */
export function GetCameraViewJSON(mapID: string, includeCamView: boolean) {
	const cameraViewRect = includeCamView ? GetViewportWorldRect(mapID) : null;
	return cameraViewRect ? {
		minX: cameraViewRect.Left, minY: cameraViewRect.Bottom,
		maxX: cameraViewRect.Right, maxY: cameraViewRect.Top,
		center: cameraViewRect.Center, // redundant, but saves a mental/math step
	} : null;
}

/** Props for the shared fixture-panel UI (below). */
export type ObjExportJSONFixturePanel_Props = {
	/** The fully-serialized JSON fixture text to display + copy. */
	json: string;
	/** Whether the "Include camera view-rect" checkbox is checked. */
	includeCamView: boolean;
	setIncludeCamView: (val: boolean)=>void;
	/** The help text shown below the fixture textarea. */
	helpText: string;
};

/** The shared JSON-fixture section: the "Include camera view-rect" checkbox, the fixture
 *  label + copy button (with its transient "Copied!" state), the fixture textarea, and the
 *  help-text row. */
export function ObjExportJSONFixturePanel({json, includeCamView, setIncludeCamView, helpText}: ObjExportJSONFixturePanel_Props) {
	const [copied, setCopied] = useState(false);

	const copyToClipboard = ()=>{
		navigator.clipboard.writeText(json).then(()=>{
			setCopied(true);
			setTimeout(()=>setCopied(false), 2000);
		}).catch(err=>console.error("Failed to copy:", err));
	};

	return <>
		<Row mt={10}>
			<CheckBox text="Include camera view-rect" value={includeCamView} onChange={val=>setIncludeCamView(val)}/>
		</Row>

		<Row mt={5} center>
			<Text style={{fontWeight: "bold"}}>JSON fixture:</Text>
			<Button ml="auto" text={copied ? "Copied!" : "Copy JSON"} onClick={copyToClipboard}
				style={copied ? {color: "limegreen"} : {}}/>
		</Row>
		<TextArea mt={5} autoSize={false} editable={false} value={json}
			style={{width: "100%", height: 300, tabSize: 2, fontFamily: "monospace", fontSize: 11}}/>

		<Row mt={8} style={{opacity: 0.6, fontSize: 11}}>
			<Text>{helpText}</Text>
		</Row>
	</>;
}
